/**
 * Invoice Model & Financial Tax Calculations
 * Handles Intra-state (CGST + SGST) vs Inter-state (IGST) tax logic,
 * as well as Non-GST Normal bills (0% Tax / Cash Memo).
 */

export const SELLER_CONFIG = {
  name: "SREE RAM TRADERS",
  gstin: "33AWFPA8086P1Z3",
  state: "Tamil Nadu",
  stateCode: "33",
  address: "33-C5, Pambalamman kovil street, Chettiyarpatti-626122",
  phone: "9865460487",
  email: "admin@webs8.com"
};

export const INVOICE_STATUS = {
  PAID: "Paid",
  UNPAID: "Unpaid",
  PARTIAL: "Partial"
};

export const INVOICE_TYPES = {
  GST: "GST",        // GST Tax Invoice (CGST+SGST or IGST)
  NORMAL: "Normal"   // Non-GST Normal Bill / Cash Memo (0% Tax)
};

export function calculateLineItem(item, qty = 1, customRate = null, isNonGst = false, qtyBreakdown = null) {
  const quantity = Number(qty || 1);
  const rate = customRate !== null && customRate !== undefined
    ? Number(customRate)
    : Number(item.basicPrice || item.sellingPrice || 0);

  // If Non-GST bill, tax rate is 0
  const gstPercent = isNonGst ? 0 : Number(item.gstPercent || 0);

  const subtotal = Math.round(quantity * rate * 100) / 100;
  const gstAmount = isNonGst ? 0 : Math.round((subtotal * gstPercent) / 100 * 100) / 100;
  const total = Math.round((subtotal + gstAmount) * 100) / 100;

  const breakdown = Array.isArray(qtyBreakdown)
    ? qtyBreakdown
    : Array.isArray(item.qtyBreakdown)
      ? item.qtyBreakdown
      : null;

  return {
    itemId: item.id || "",
    itemName: item.itemName || item.name || "",
    itemType: item.itemType || "goods",
    gstCode: item.gstCode || item.sku || "",
    unit: item.unit || "pcs",
    qty: quantity,
    rate,
    gstPercent,
    lineSubtotal: subtotal,
    lineGst: gstAmount,
    lineTotal: total,
    qtyBreakdown: breakdown
  };
}

export function calculateInvoiceTotals(lineItems = [], customer = null, discountAmount = 0, invoiceType = "GST", company = null) {
  const isNonGst = invoiceType === INVOICE_TYPES.NORMAL;
  const sellerStateCode = company?.stateCode || SELLER_CONFIG.stateCode || "33";
  const customerStateCode = customer?.stateCode || (customer?.gstin ? customer.gstin.substring(0, 2) : "33");
  const isInterState = !isNonGst && customerStateCode && customerStateCode !== sellerStateCode;

  let subtotal = 0;
  let gstTotal = 0;

  lineItems.forEach((li) => {
    subtotal += Number(li.lineSubtotal || 0);
    if (!isNonGst) {
      gstTotal += Number(li.lineGst || 0);
    }
  });

  const discount = Math.min(Number(discountAmount || 0), subtotal);
  const discountedSubtotal = subtotal - discount;

  let finalGst = 0;
  let cgst = 0;
  let sgst = 0;
  let igst = 0;

  if (!isNonGst) {
    // Recalculate tax if discount is applied across lines proportionally
    finalGst = gstTotal;
    if (discount > 0 && subtotal > 0) {
      const ratio = discountedSubtotal / subtotal;
      finalGst = Math.round(gstTotal * ratio * 100) / 100;
    }

    if (isInterState) {
      igst = finalGst;
    } else {
      cgst = Math.round((finalGst / 2) * 100) / 100;
      sgst = Math.round((finalGst - cgst) * 100) / 100;
    }
  }

  const rawTotal = discountedSubtotal + finalGst;
  const grandTotal = Math.round(rawTotal);
  const roundOff = Math.round((grandTotal - rawTotal) * 100) / 100;

  return {
    subtotal: Math.round(subtotal * 100) / 100,
    discountAmount: Math.round(discount * 100) / 100,
    taxableAmount: Math.round(discountedSubtotal * 100) / 100,
    gstTotal: finalGst,
    cgst,
    sgst,
    igst,
    isInterState,
    isNonGst,
    roundOff,
    grandTotal,
    balance: grandTotal
  };
}

export function generateInvoiceNumber(invoiceType = "GST", company = null) {
  const isNonGst = invoiceType === INVOICE_TYPES.NORMAL || invoiceType === "Normal";
  if (company) {
    if (isNonGst) {
      const prefix = company.nonGstInvoicePrefix !== undefined && company.nonGstInvoicePrefix !== null
        ? company.nonGstInvoicePrefix
        : "";
      const nextNum = (company.nonGstNextInvoiceNumber !== undefined && company.nonGstNextInvoiceNumber !== null && company.nonGstNextInvoiceNumber !== "")
        ? company.nonGstNextInvoiceNumber
        : 1;
      return `${prefix}${nextNum}`;
    }
    const prefix = company.invoicePrefix !== undefined && company.invoicePrefix !== null
      ? company.invoicePrefix
      : "";
    const nextNum = (company.nextInvoiceNumber !== undefined && company.nextInvoiceNumber !== null && company.nextInvoiceNumber !== "")
      ? company.nextInvoiceNumber
      : 1;
    return `${prefix}${nextNum}`;
  }
  const date = new Date();
  const year = date.getFullYear().toString().slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const random = Math.floor(1000 + Math.random() * 9000);
  const prefix = isNonGst ? "BILL" : "INV";
  return `${prefix}-${year}${month}-${random}`;
}
